import { ArrowRight } from "lucide-react";
import { RuntimeLink } from "../../runtime/RendererRuntime";
import type { LinkTarget } from "../../types";
import { resolveLinkHref } from "../../utils/links";

/** A single labelled metric on a stat card. Value is already formatted
 *  for display (the projector handles the locale, decimals, sign prefix
 *  and unit suffix). */
export type StatCardStat = {
  label: string;
  value: string;
  unit?: string;
};

export type StatCardProps = {
  /** Date or other heading — sits above the metric stack. */
  date: string;
  /** Optional small badge to the right of the date (e.g. data source). */
  source?: string;
  /** The metric stack. The first stat renders as the hero value; the
   *  remaining stats render as a smaller secondary row. */
  stats: StatCardStat[];
  href: LinkTarget;
};

/**
 * Data-first card used by `PaginatedActivityGrid` when `cardType === "stat"`.
 *
 * Built for index pages over numeric data records (taux d'inflation
 * hebdomadaire, taux interbancaire, cours parallèle…) where the record
 * has no meaningful title — the value IS the headline. The card shows:
 *   - date at the top, optional source badge
 *   - the first stat as the hero number (large, brand blue)
 *   - additional stats as a compact secondary row with smaller text
 *   - subtle chevron in the corner so the whole card reads as a link
 *
 * Reuses the same compact card chrome as `ArticleCardCompact` (white bg,
 * 1px slate ring, hover ring tightens to brand blue) so the two cards
 * read as part of one design system across index pages.
 */
export function StatCard({ date, source, stats, href }: StatCardProps) {
  const safeHref = resolveLinkHref(href) ?? "/";
  const [hero, ...rest] = stats ?? [];
  return (
    <RuntimeLink
      href={safeHref}
      className="group block rounded-md bg-white p-5 ring-1 ring-slate-200 transition hover:ring-[#00B862]/40"
    >
      <article className="flex h-full flex-col gap-3">
        <header className="flex items-start justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {date}
          </span>
          {source ? (
            <span className="inline-flex items-center rounded-full bg-[#E6F4EA] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#00B862]">
              {source}
            </span>
          ) : null}
        </header>

        {hero ? (
          <div>
            <div className="text-xs font-medium text-slate-600">{hero.label}</div>
            <div className="mt-1 flex items-baseline gap-1 leading-none">
              <span className="text-3xl font-bold tabular-nums text-[#00B862]">{hero.value}</span>
              {hero.unit ? <span className="text-base font-semibold text-[#121212]/70">{hero.unit}</span> : null}
            </div>
          </div>
        ) : null}

        {rest.length > 0 ? (
          <dl className="mt-auto grid grid-cols-1 gap-1 pt-1 text-xs text-slate-600 sm:grid-cols-2 sm:gap-x-4">
            {rest.map((s, i) => (
              <div key={`${s.label}-${i}`} className="flex items-baseline justify-between gap-2">
                <dt className="truncate font-medium text-slate-500">{s.label}</dt>
                <dd className="shrink-0 font-semibold tabular-nums text-[#121212]">
                  {s.value}
                  {s.unit ? <span className="ml-0.5 font-normal text-slate-500">{s.unit}</span> : null}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        <div className="mt-1 flex items-center justify-end text-[#00B862] opacity-0 transition group-hover:opacity-100">
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </div>
      </article>
    </RuntimeLink>
  );
}
